import * as React from 'react';
import {Alert, Button, Form, Modal, Select} from 'antd';
import ExchangeRateRefreshButton from '../common/ExchangeRateRefreshButton';
import {FormComponentProps} from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';

import {
  IncomingInvoice,
  InventoryStockItem,
  InventoryTransportLetter,
  SaveInventoryStockItemRequest,
  Warehouse
} from '../api/dtos/dtos';
import locales from '../locales';
import NumberFormItem from '../common/form-items/NumberFormItem';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import * as formUtils from '../utils/form-utils';
import IncomingInvoiceSelector from './IncomingInvoiceSelector';
import {formatDate} from '../utils/date-utils';
import {
  groupNumber,
  numberGroupFormatter,
  numberGroupParser,
  renderFixedPriceColumn,
  withCurrency
} from '../utils/number-utils';
import * as paths from '../paths';
import {Link} from 'react-router-dom';
import HiddenFormItem from "../common/form-items/HiddenFormItem";
import SelectFormItem from "../common/form-items/SelectFormItem";
import StaticFormItem from "../common/form-items/StaticFormItem";
import {calculateHufSum, CURRENCY_EUR, CURRENCY_HUF, getCurrencyOptions} from "../utils/currency-utils";
import InventoryTransportLetterSelector from './InventoryTransportLetterSelector';

interface Props extends FormComponentProps {
  item?: InventoryStockItem;
  warehouses: Warehouse[];
  onRefreshExchangeRate: () => Promise<number | void>;
  onFormSubmit: (request: SaveInventoryStockItemRequest) => void;
  onFormCancel: () => void;
}

interface State {
  selectorVisible: boolean;
  selectorType: string;
  selectedInvoice?: IncomingInvoice;
  selectedTransportLetter?: InventoryTransportLetter;
  cost: number;
  profitMargin: number;
  currency: string;
  exchangeRate?: number;
}

const SOURCE_INCOMING_INVOICE = 'INCOMING_INVOICE';
const SOURCE_TRANSPORT_LETTER = 'TRANSPORT_LETTER';

const FIELD_INCOMING_INVOICE = 'incomingInvoiceId';
const FIELD_INVENTORY_TRANSPORT_LETTER = 'inventoryTransportLetterId';
const FIELD_SOURCE = 'source';
const FIELD_COUNT = 'count';
const FIELD_COST = 'cost';
const FIELD_CURRENCY = 'currency';
const FIELD_EXCHANGE_RATE = 'exchangeRate';
const FIELD_PROFIT_MARGIN = 'profitMargin';
const FIELD_WAREHOUSE = 'warehouseName';

class InventoryStockItemForm extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    const selectedInvoice = props.item?.incomingInvoice;
    const selectedTransportLetter = props.item?.inventoryTransportLetter;
    this.state = {
      selectorVisible: false,
      selectorType: selectedTransportLetter ? SOURCE_TRANSPORT_LETTER : SOURCE_INCOMING_INVOICE,
      selectedInvoice,
      selectedTransportLetter,
      cost: props.item?.cost || 0,
      profitMargin: props.item?.profitMargin || 0,
      currency: selectedInvoice?.currency || props.item?.currency || CURRENCY_HUF,
      exchangeRate: selectedInvoice?.exchangeRate || props.item?.exchangeRate,
    };
  }

  public render() {
    const {item, form, warehouses} = this.props;
    const {
      selectedInvoice,
      selectedTransportLetter,
      selectorVisible,
      selectorType,
      exchangeRate,
      currency
    } = this.state;
    const currencyDisabled = selectedInvoice !== undefined && selectorType === SOURCE_INCOMING_INVOICE;
    const selectedCurrency = currencyDisabled && selectedInvoice ? selectedInvoice.currency : currency;
    const isNonHuf = selectedCurrency !== CURRENCY_HUF;
    const currencyOption = getCurrencyOptions().find(opt => opt.value === selectedCurrency);

    const sumPrecision = currencyOption?.fraction ? 2 : 0;
    const sum = form.getFieldValue(FIELD_COST);
    const hufSum = calculateHufSum(sum, selectedCurrency, exchangeRate);

    return (
      <Form>
        <SelectFormItem
          form={form}
          fieldId={FIELD_SOURCE}
          label={locales.inventory_stock_item_source}
          initialValue={selectorType}
          required={true}
          options={[
            {value: SOURCE_INCOMING_INVOICE, title: locales.incoming_invoice},
            {value: SOURCE_TRANSPORT_LETTER, title: locales.transport_letter},
          ]}
          onSelect={this.onSourceSelect}
        />

        {selectorType === SOURCE_INCOMING_INVOICE && (
          <HiddenFormItem
            form={form}
            fieldId={FIELD_INCOMING_INVOICE}
            label={locales.incoming_invoice}
            initialValue={selectedInvoice ? selectedInvoice.id : undefined}
            required={true}
          >
            {!selectedInvoice &&
                <Button icon="file-search" onClick={this.showSelector}>{locales.select_incoming_invoice}</Button>}
            {selectedInvoice && this.renderSelectedInvoiceAlert(selectedInvoice)}
          </HiddenFormItem>
        )}

        {selectorType === SOURCE_TRANSPORT_LETTER && (
          <HiddenFormItem
            form={form}
            fieldId={FIELD_INVENTORY_TRANSPORT_LETTER}
            label={locales.transport_letter}
            initialValue={selectedTransportLetter ? selectedTransportLetter.id : undefined}
            required={true}
          >
            {!selectedTransportLetter &&
                <Button icon="file-search" onClick={this.showSelector}>{locales.transport_letter_select}</Button>}
            {selectedTransportLetter && this.renderSelectedTransportLetterAlert(selectedTransportLetter)}
          </HiddenFormItem>
        )}

        <NumberFormItem
          form={form}
          fieldId={FIELD_COUNT}
          label={locales.count}
          initialValue={item ? item.count : null}
          required={true}
          min={1}
          precision={0}
          formatter={numberGroupFormatter}
        />

        <NumberFormItem
          form={form}
          fieldId={FIELD_COST}
          label={locales.initial_cost}
          initialValue={item ? item.cost : null}
          required={true}
          min={0}
          max={1_000_000_000}
          onChange={this.onCostChanged}
          precision={sumPrecision}
          parser={numberGroupParser}
          formatter={numberGroupFormatter}
        >
          <span style={{marginLeft: 8, display: 'inline-block'}}>
            {form.getFieldDecorator(FIELD_CURRENCY, {
              initialValue: selectedCurrency,
              rules: [{required: true, message: locales.mandatory}],
            })(
              <Select style={{width: 64}} disabled={currencyDisabled} onChange={this.onCurrencyChanged}>
                {getCurrencyOptions().map(opt =>
                  <Select.Option key={opt.key} value={opt.value}>{opt.title}</Select.Option>
                )}
              </Select>
            )}
          </span>
        </NumberFormItem>
        {isNonHuf &&
            <NumberFormItem
                form={form}
                fieldId={FIELD_EXCHANGE_RATE}
                label={locales.exchange_rate}
                initialValue={item ? item.exchangeRate : exchangeRate}
                required={true}
                min={0}
                precision={2}
                disabled={currencyDisabled}
                onChange={this.onExchangeRateChanged}
                parser={numberGroupParser}
                formatter={numberGroupFormatter}
            >
              {selectedCurrency === CURRENCY_EUR &&
                <ExchangeRateRefreshButton
                  tooltip={locales.item_exchange_rate_refresh_tooltip}
                  disabled={currencyDisabled}
                  onRefresh={this.refreshExchangeRate}
                />
              }
            </NumberFormItem>
        }
        {isNonHuf &&
            <StaticFormItem
                label={locales.huf_sum}
                initialValue={hufSum ? <>{groupNumber(hufSum)} {locales.app_currency}</> : '-'}
            />
        }

        <NumberFormItem
          form={form}
          fieldId={FIELD_PROFIT_MARGIN}
          label={locales.profit_margin_percent}
          initialValue={item ? item.profitMargin : null}
          required={true}
          min={0}
          precision={0}
          onChange={this.onProfitMarginChanged}
          formatter={numberGroupFormatter}
        >
          <> %</>
        </NumberFormItem>

        <FormItem {...formUtils.formItemLayout} label={locales.selling_price}>
          <span className="ant-form-text">{renderFixedPriceColumn(this.getSellingPrice())}</span>
        </FormItem>

        <SelectFormItem
          form={form}
          fieldId={FIELD_WAREHOUSE}
          label={locales.warehouse}
          initialValue={warehouses.find(w => w.isDefault)?.id}
          required={true}
          options={warehouses.map(w => ({value: w.id, key: w.id, title: w.name}))}
        />

        <SubmitCancelFormItem onSubmit={this.onSubmit} onCancel={this.props.onFormCancel}/>

        <Modal
          title={selectorType === SOURCE_INCOMING_INVOICE ? locales.select_incoming_invoice : locales.transport_letters}
          visible={selectorVisible}
          footer={null}
          onCancel={this.hideSelector}
          width="auto"
          style={{margin: '0 24px'}}
          destroyOnClose={true}
        >
          {selectorType === SOURCE_INCOMING_INVOICE && <IncomingInvoiceSelector onSelect={this.onInvoiceSelected}/>}
          {selectorType === SOURCE_TRANSPORT_LETTER &&
              <InventoryTransportLetterSelector onSelect={this.onTransportLetterSelected}/>}
        </Modal>
      </Form>
    );
  }

  private renderSelectedInvoiceAlert = (invoice: IncomingInvoice): React.ReactNode => {
    const content = (
      <table>
        <tbody>
        {this.alertRow(locales.invoice_number, <Link
          to={paths.viewIncomingInvoicePath(invoice.id)}>{invoice.invoiceNumber}</Link>)}
        {this.alertRow(locales.appellation, invoice.name)}
        {this.alertRow(locales.company_name, invoice.companyName)}
        {this.alertRow(locales.issue_date, formatDate(invoice.issueDate))}
        {this.alertRow(locales.sum, withCurrency(groupNumber(invoice.sum)))}
        </tbody>
      </table>
    );

    return (
      <Alert
        style={{marginBottom: 16}}
        type="info"
        message={content}
        closable={true}
        onClose={this.onInvoiceCleared}
      />
    );
  };

  private renderSelectedTransportLetterAlert = (letter: InventoryTransportLetter): React.ReactNode => {
    const content = (
      <table>
        <tbody>
        {this.alertRow(
          locales.id, <Link
            to={paths.viewInventoryTransportLetterPath(letter.id!)}>{letter.transportLetterNumber}</Link>)}
        {this.alertRow(locales.company_name, letter.partner?.name || undefined)}
        {this.alertRow(locales.date, letter.date ? formatDate(letter.date) : undefined)}
        {this.alertRow(locales.responsible_user, letter.responsibleUser || undefined)}
        {this.alertRow(locales.incoming_invoice, letter.incomingInvoice?.invoiceNumber || undefined)}
        </tbody>
      </table>
    );

    return (
      <Alert
        style={{marginBottom: 16}}
        type="info"
        message={content}
        closable={true}
        onClose={this.onTransportLetterCleared}
      />
    );
  };

  private alertRow = (title: string, value?: React.ReactNode) => {
    if (!value) {
      return null;
    }
    return (
      <tr>
        <td style={{textAlign: 'right', paddingRight: 8}}><b>{title}</b></td>
        <td>{value}</td>
      </tr>
    );
  };

  private showSelector = () => {
    this.setState({selectorVisible: true});
  };

  private hideSelector = () => {
    this.setState({selectorVisible: false});
  };

  private onInvoiceSelected = (invoice: IncomingInvoice) => {
    this.setState({
      selectedInvoice: invoice,
      selectorVisible: false,
      currency: invoice.currency,
      exchangeRate: invoice.exchangeRate,
    }, () => {
      this.props.form.setFieldsValue({[FIELD_INCOMING_INVOICE]: invoice.id});
      this.props.form.setFieldsValue({[FIELD_CURRENCY]: invoice.currency});
      this.props.form.setFieldsValue({[FIELD_EXCHANGE_RATE]: invoice.exchangeRate});
      this.onExchangeRateChanged(invoice.exchangeRate);
      this.props.form.validateFields([FIELD_INCOMING_INVOICE], {force: true}, () => undefined);
    });
  };

  private onTransportLetterSelected = (letter: InventoryTransportLetter) => {
    this.setState({
      selectedTransportLetter: letter,
      selectorVisible: false,
    }, () => {
      this.props.form.setFieldsValue({[FIELD_INVENTORY_TRANSPORT_LETTER]: letter.id});
      this.props.form.validateFields([FIELD_INVENTORY_TRANSPORT_LETTER], {force: true}, () => undefined);
    });
  };

  private onInvoiceCleared = () => {
    this.setState({selectedInvoice: undefined}, () => {
      this.props.form.setFieldsValue({[FIELD_INCOMING_INVOICE]: undefined});
      this.props.form.validateFields([FIELD_INCOMING_INVOICE], {force: true}, () => undefined);
    });
  };

  private onTransportLetterCleared = () => {
    this.setState({selectedTransportLetter: undefined}, () => {
      this.props.form.setFieldsValue({[FIELD_INVENTORY_TRANSPORT_LETTER]: undefined});
      this.props.form.validateFields([FIELD_INVENTORY_TRANSPORT_LETTER], {force: true}, () => undefined);
    });
  };

  private onSourceSelect = (value: string) => {
    this.setState({selectorType: value, selectorVisible: false}, () => {
      if (value === SOURCE_INCOMING_INVOICE) {
        this.setState({selectedTransportLetter: undefined});
        this.props.form.setFieldsValue({[FIELD_INVENTORY_TRANSPORT_LETTER]: undefined});
        this.props.form.validateFields([FIELD_INCOMING_INVOICE], {force: true}, () => undefined);
      } else {
        this.setState({selectedInvoice: undefined});
        this.props.form.setFieldsValue({[FIELD_INCOMING_INVOICE]: undefined});
        this.props.form.validateFields([FIELD_INVENTORY_TRANSPORT_LETTER], {force: true}, () => undefined);
      }
    });
  };

  private onCostChanged = (cost: number | undefined) => {
    this.setState({cost: cost || 0});
  };

  private onProfitMarginChanged = (profitMargin: number | undefined) => {
    this.setState({profitMargin: profitMargin || 0});
  };

  private onExchangeRateChanged = (exchangeRate: number | undefined) => {
    this.setState({exchangeRate});
  };

  private refreshExchangeRate = () => {
    return this.props.onRefreshExchangeRate()
      .then(rate => {
        const {selectedInvoice, selectorType} = this.state;
        const currencyDisabled = selectedInvoice !== undefined && selectorType === SOURCE_INCOMING_INVOICE;
        if (rate != null && !currencyDisabled && this.props.form.getFieldValue(FIELD_CURRENCY) === CURRENCY_EUR) {
          this.props.form.setFieldsValue({[FIELD_EXCHANGE_RATE]: rate});
          this.onExchangeRateChanged(rate);
        }
      });
  };

  private onCurrencyChanged = (currency: string) => {
    this.setState({currency});
  };

  private getSellingPrice = (): number => {
    const {cost, profitMargin, exchangeRate, currency} = this.state;
    const multiplier = currency === CURRENCY_HUF ? 1 : (exchangeRate != null ? exchangeRate : 1);
    const costWithExchangeRate = cost * multiplier;
    return costWithExchangeRate * (1 + profitMargin / 100);
  };

  private onSubmit = () => {
    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.props.onFormSubmit({
          inventoryStockId: 0,
          incomingInvoiceId: values[FIELD_INCOMING_INVOICE],
          inventoryTransportLetterId: values[FIELD_INVENTORY_TRANSPORT_LETTER],
          count: values[FIELD_COUNT],
          cost: values[FIELD_COST],
          profitMargin: values[FIELD_PROFIT_MARGIN],
          warehouseId: values[FIELD_WAREHOUSE],
          currency: values[FIELD_CURRENCY],
          exchangeRate: values[FIELD_EXCHANGE_RATE],
          hufCost: calculateHufSum(values[FIELD_COST], values[FIELD_CURRENCY], values[FIELD_EXCHANGE_RATE]) || values[FIELD_COST],
        });
      }
    });
  };
}

export default Form.create<Props>()(InventoryStockItemForm);



// WEBPACK FOOTER //
// ./src/inventory-stock-details/InventoryStockItemForm.tsx